import {EntityListFilter} from 'components';
import {useEffect, useMemo, useState} from 'react';
import {VexWorkbenchItem} from './VexWorkbenchItems';
import {RegulatoryDocumentStatus} from 'types';
import {useUserContext} from 'authentication/UserContext';

export const useVexWorkbenchItemsFilter = (items: VexWorkbenchItem[]) => {
	const [filteredItems, setFilteredItems] = useState<VexWorkbenchItem[]>([]);
	const [filters, setFilters] = useState<EntityListFilter[]>([]);
	const {myVexClusters} = useUserContext();

	useEffect(() => {
		const marketFilters = filters
			.filter(f => f.columnKey === 'LegalArea')
			.map(f => f.selection);
		let newItems = items;
		if (marketFilters.length > 0) {
			newItems = items.filter(i =>
				i.regulatoryDocument?.regulation?.markets?.some(
					m => marketFilters.indexOf(m.name) !== -1,
				),
			);
		}

		const statusFilters = filters
			.filter(f => f.columnKey === 'Status')
			.map(f => f.selection);

		if (statusFilters.length > 0) {
			newItems = newItems.filter(
				i => statusFilters.indexOf(i.regulatoryDocument?.status) !== -1,
			);
		}

		const vexClusterFilters = filters
			.filter(f => f.columnKey === 'VexCluster')
			.map(f => f.selection);

		if (vexClusterFilters.length > 0) {
			newItems = newItems.filter(i =>
				i.regulatoryDocument?.paragraphs?.some(paragraph =>
					paragraph.keywordAssignments?.some(ka =>
						ka.vexClusterAssignments?.some(
							vca =>
								vca?.vexCluster &&
								vexClusterFilters.indexOf(vca.vexCluster.name) !== -1,
						),
					),
				),
			);
		}
		setFilteredItems(newItems);
	}, [items, filters]);

	const filterLegalAreas = useMemo(() => {
		const markets = items.flatMap(
			i =>
				i.regulatoryDocument.regulation?.markets?.map(m => {
					return {name: m.name, optionKey: m.name};
				}) || [],
		);
		return markets.filter(
			(market, index, self) =>
				self.findIndex(t => t.name === market.name) === index,
		);
	}, [items]);

	const filterStatuses = useMemo(() => {
		const statuses = items
			.map(i => i.regulatoryDocument?.status as RegulatoryDocumentStatus)
			.map(s => {
				return {name: s, optionKey: s};
			});
		return statuses.filter(
			(status, index, self) =>
				self.findIndex(t => t.name === status.name) === index,
		);
	}, [items]);

	const filterVexClusters = useMemo(() => {
		const clusters = items
			.flatMap(i =>
				i.regulatoryDocument?.paragraphs?.flatMap(par =>
					par.keywordAssignments?.flatMap(ka =>
						ka.vexClusterAssignments?.flatMap(vca => vca?.vexCluster),
					),
				),
			)
			.filter(
				vca => vca !== undefined && myVexClusters.some(vc => vc.id === vca!.id),
			)
			.map(vca => {
				return {name: vca!.name, optionKey: vca!.name};
			});
		return clusters.filter(
			(cluster, index, self) =>
				self.findIndex(c => c.name === cluster.name) === index,
		);
	}, [items, myVexClusters]);

	return {
		filteredItems,
		filters,
		setFilters,
		filterLegalAreas,
		filterStatuses,
		filterVexClusters,
	};
};
